import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { Redirect } from 'react-router-dom';

import EventOnlineView from '../components/content/event/EventOnlineView';
import Faq from '../components/content/Faq';
import InvalidGpxFileInformation from '../components/content/informations/InvalidGpxFileInformation';
import LoadingView from '../components/content/LoadingView';
import GpxMediaList from '../components/content/lists/GpxMediaList';
import withRequestHandler from '../components/hoc/withRequestHandler';
import { UserContext } from '../components/context/UserContext';
import EventCommonPart from '../components/content/event/EventCommonPart';
import EventSearchMainActions from '../components/content/event/EventSearchMainActions';

import { getMainSearchResultUrl } from '../utils/common';
import { includesFotoFlat } from '../utils/common';
import { MAIN_SEARCH_FACE } from '../utils/variables';
import { customScrollToComponent } from '../utils/common';

////////////////////////////////////////////
// TODO MISSING TESTS: related to useEffect and refetch new media (Relay errors)
////////////////////////////////////////////

const EventGpsSearchPage = ({
  viewer,
  gpxFileIdFromUrl,
  snFromUrl,
  relay,
  onRequestStart,
  onRequestStop,
  eventHasSecondFactor,
  isLoading,
  customerKey,
  history,
  codeSecondFactorForFaceEvent,
}) => {
  const { event } = viewer;
  const [showInvalidGpxFileInformation, setShowInvalidGpxFileInformation] =
    useState(false);
  const [searchFormPosition, setSearchFormPosition] = useState(null);
  const { onMarathonFotoChange } = useContext(UserContext);
  const [altSearchPosition, setAltSearchPosition] = useState(null);
  const [shouldShowMainSearch, setShouldShowMainSearch] = useState(false);
  const [elementToAnimate, setElementToAnimate] = useState(null);

  const eventOnlineView = useRef();
  const mediaSection = useRef();
  const participantMediaCount = event.participant
    ? event.participant.media.mediaInfo.count
    : 0;

  useEffect(() => {
    onMarathonFotoChange(event.marathonFoto);
    scrollToMediaSection();
  }, []);

  useEffect(() => {
    refetchMediaForNewGpxFile(gpxFileIdFromUrl);
    scrollToMediaSection();
  }, [gpxFileIdFromUrl]);

  const goBackToMainSearchResults = () => {
    const destinationUrl = getMainSearchResultUrl(
      event.sgId,
      snFromUrl,
      eventHasSecondFactor,
      customerKey
    );
    history.push(destinationUrl);
  };

  const scrollToForm = () => {
    searchFormPosition && customScrollToComponent(searchFormPosition.current);
  };

  const onScrollToMainSearch = () => {
    setShouldShowMainSearch(true);
    scrollToForm();
    setTimeout(() => {
      setShouldShowMainSearch(false);
    }, 100);
  };

  const liftingUpFormPositionData = (formPositionRef) => {
    setSearchFormPosition(formPositionRef);
  };

  const scrollToMediaSection = () => {
    if (mediaSection.current) {
      customScrollToComponent(mediaSection.current);
    }
  };

  const liftingUpAltSearchPositionData = (altSerachPositionRef) => {
    setAltSearchPosition(altSerachPositionRef);
  };

  const scrollToAltSearch = () => {
    altSearchPosition && customScrollToComponent(altSearchPosition.current);
  };

  const refetchMediaForNewGpxFile = (newGpxFileId) => {
    onRequestStart();
    setShowInvalidGpxFileInformation(false);
    relay.refetch({ gpxFileId: newGpxFileId }, null, (err) => {
      if (err) {
        onRequestStop();
        setShowInvalidGpxFileInformation(true);
      } else {
        onRequestStop();
      }
    });
  };

  if (event.state === 'reminder') {
    return <Redirect to={`/event/${event.sgId}`} />;
  }

  return (
    <div className="container-960 container-page">
      <EventCommonPart event={event}>
        <EventSearchMainActions
          onFindMorePhotosClick={scrollToAltSearch}
          event={event}
          onGoBackToMainSearch={goBackToMainSearchResults}
          onScrollToForm={onScrollToMainSearch}
          participantMediaCount={participantMediaCount}
          setElementToAnimate={setElementToAnimate}
        />
        <EventOnlineView
          ref={eventOnlineView}
          onlineEvent={event}
          urlSn={snFromUrl}
          customerKey={customerKey}
          hasSmartSearchTab={event.smartSearch && participantMediaCount !== 0}
          hasGpsSearchTab={true}
          hasDualSearchTab={event.searchDual}
          setFormRef={liftingUpFormPositionData}
          codeSecondFactorForFaceEvent={codeSecondFactorForFaceEvent}
          showStillProcessingHint={event.pendingMediaProcessing}
          goBackToMainSearchResults={goBackToMainSearchResults}
          setAltSearchRef={liftingUpAltSearchPositionData}
          shouldShowMainSearch={shouldShowMainSearch}
          elementToAnimate={elementToAnimate}
        />
      </EventCommonPart>
      <div ref={mediaSection}>
        {isLoading && <LoadingView />}
        {!isLoading && (
          <>
            {!showInvalidGpxFileInformation &&
              event.searchMediaByGpx.mediaInfo.count !== 0 && (
                <GpxMediaList
                  eventHasSearchByFace={event.mainSearch === MAIN_SEARCH_FACE}
                  eventHasSecondFactor={eventHasSecondFactor}
                  eventId={event.sgId}
                  startnumber={snFromUrl}
                  gpxFileId={gpxFileIdFromUrl}
                  gpxFileMedia={event.searchMediaByGpx}
                  offer={event}
                  participantMediaCount={participantMediaCount}
                  goBackToMainSearchResults={goBackToMainSearchResults}
                  onScrollToMainSearch={scrollToForm}
                  eventHasFotoFlat={includesFotoFlat(event.products)}
                />
              )}
            {(showInvalidGpxFileInformation ||
              event.searchMediaByGpx.mediaInfo.count === 0) && (
              <InvalidGpxFileInformation />
            )}
          </>
        )}
      </div>
      <Faq onFindMore={() => scrollToAltSearch()} />
    </div>
  );
};

EventGpsSearchPage.propTypes = {
  customerKey: PropTypes.string,
  eventHasSecondFactor: PropTypes.bool,
  isLoading: PropTypes.bool,
  snFromUrl: PropTypes.string,
  gpxFileIdFromUrl: PropTypes.string,
  viewer: PropTypes.object,
  relay: PropTypes.object,
  onRequestStart: PropTypes.func,
  onRequestStop: PropTypes.func,
  codeSecondFactorForFaceEvent: PropTypes.string,
  history: PropTypes.object,
};

export { EventGpsSearchPage };

export default createRefetchContainer(
  withRequestHandler(EventGpsSearchPage),
  {
    viewer: graphql`
      fragment EventGpsSearchPage_viewer on Viewer {
        event(id: $eventId, language: $language) {
          id
          sgId
          gps
          hostResultUrl
          products {
            type
          }
          pendingMediaProcessing

          participant(startnumber: $startnumber, secondFactor: $secondFactor)
            @skip(if: $noStartnumber) {
            media {
              mediaInfo {
                count
              }
            }
            startnumber
          }
          searchMediaByGpx(gpxFileId: $gpxFileId) {
            mediaInfo {
              count
            }
            ...GpxMediaList_gpxFileMedia
          }
          secondFactorType {
            type
          }
          mainSearch
          searchDual
          smartSearch
          state
          marathonFoto
          ...EventOnlineView_onlineEvent
          ...GpxMediaList_offer
          ...EventCommonPart_event
          ...EventSearchMainActions_event
        }
      }
    `,
  },
  graphql`
    query EventGpsSearchPageRefetchQuery(
      $eventId: ID!
      $language: String
      $startnumber: ID!
      $gpxFileId: ID!
      $count: Int!
      $cursor: String
      $secondFactor: String
      $individualPart: String
      $noStartnumber: Boolean
      $isRecheckPage: Boolean!
    ) {
      viewer {
        ...EventGpsSearchPage_viewer
      }
    }
  `
);
